import { useEffect, useState } from 'react';
import { rootStore } from '../../shared/stores/root-store';
import { OFF, parseFlag, eligible } from './flag';
import { platformNewCabinet } from '../../shared/stores/new-cabinet';
import type { OpsTodayFlag } from './flag';

/**
 * Доступен ли пользователю экран «Сегодня».
 *
 * Файл перечитывается раз в 15 секунд и при возврате на вкладку —
 * чтобы выключение пилота доходило до открытого браузера без
 * перезагрузки страницы. Любая ошибка чтения означает «выключено»:
 * недоступный конфиг не должен показывать экспериментальный раздел.
 */
export function useOpsTodayFlag(): boolean {
  return useOpsTodayFlagState().allowed;
}

/** То же плюс «файл уже прочитан» — чтобы не мигать прежним экраном до ответа. */
export function useOpsTodayFlagState(): { allowed: boolean; loaded: boolean } {
  const [flag, setFlag] = useState<OpsTodayFlag>(OFF);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    let controller: AbortController | undefined;

    const refresh = async () => {
      controller?.abort();
      controller = new AbortController();
      const own = controller;
      const timeout = window.setTimeout(() => own.abort(), 3000);
      try {
        const response = await fetch('/ops-today.json', {
          cache: 'no-store',
          signal: own.signal,
        });
        if (!response.ok) throw new Error('Config unavailable');
        const next = parseFlag(await response.json());
        if (active) setFlag(next);
      } catch {
        if (active) setFlag(OFF);
      } finally {
        clearTimeout(timeout);
        if (active) setLoaded(true);
      }
    };

    void refresh();
    const timer = window.setInterval(refresh, 15000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      controller?.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const account = Number(rootStore.profile?.id) || undefined;
  const platform = rootStore.platforms.activePlatform?.id;
  const allowed = eligible(flag, account, platform) || platformNewCabinet(platform);

  // Почему раздела не видно — в консоли, а не в догадках.
  //
  // Пилот прячется молча, и это правильно для боевого отеля. Но на
  // стенде «пункта меню нет» означает сразу четыре разные причины:
  // файл не доехал, площадка не в списке, учётка не в списке, файл
  // битый. Один раз мы уже потратили на это полдня, разбирая
  // «Раздел недоступен» без единой подсказки.
  useEffect(() => {
    if (allowed) return;
    console.info('[ops-today] раздел скрыт', {
      причина: !flag.enabled
        ? 'флаг выключен или файл не прочитался'
        : !account
          ? 'профиль ещё не загружен'
          : !platform
            ? 'площадка не выбрана'
            : !flag.platformIds.includes(platform)
              ? 'площадка не в списке разрешённых'
              : 'учётка не в списке разрешённых',
      площадка: platform,
      учётка: account,
      флаг: flag,
    });
  }, [allowed, flag, account, platform]);

  return { allowed, loaded };
}
